import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { FORMATIONS } from "../lib/constants";
import PitchBoard from "../components/PitchBoard";
import PlayerNoteAdder from "../components/PlayerNoteAdder";
import { toast } from "sonner";
import { CirclePlus, X, Trash, Medal, MapPinned, LoaderCircle, Replace, Goal, RectangleVertical } from "lucide-react";

const inputCls = "w-full px-3 py-2.5 rounded-xl bg-white border border-[#d1fae5] focus:border-[#2a3b19] outline-none text-sm";

const emptyMatch = {
  opponent: "", stadium: "", date: "", time: "", tournament: "", formation: "4-3-3",
  squad: [], starting_xi: [], substitutes: [], tactic_notes: "",
  substitutions: [], goals: [], cards: [], our_score: "", opp_score: "",
  evaluations: [], general_notes: "",
};

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ opponent: "", tournament: "", stadium: "", date: "", time: "", formation: "4-3-3" });
  const [tab, setTab] = useState("info");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [m, p] = await Promise.all([api.get("/matches"), api.get("/players")]);
    setMatches(m.data);
    setPlayers(p.data);
  };
  useEffect(() => { load(); }, []);

  const openDetails = (m) => {
    setTab("info");
    setEditing({ ...emptyMatch, ...m, our_score: m.our_score == null ? "" : m.our_score, opp_score: m.opp_score == null ? "" : m.opp_score, evaluations: (m.evaluations || []).map((e) => ({ ...e, rating: e.rating == null ? "" : e.rating })) });
  };

  const startCreate = () => {
    setCreateForm({ opponent: "", tournament: "", stadium: "", date: "", time: "", formation: "4-3-3" });
    setCreating(true);
  };

  const createMatch = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/matches", { ...emptyMatch, ...createForm, our_score: null, opp_score: null });
      toast.success("تم إنشاء المباراة، أضف التفاصيل الآن");
      setCreating(false);
      await load();
      openDetails(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const applyFormation = (formation) => {
    const coords = FORMATIONS[formation];
    const assigned = (editing.starting_xi.length ? editing.starting_xi : []).slice(0, 11);
    const newXI = coords.map((c, i) => ({ player_id: assigned[i]?.player_id || null, x: c.x, y: c.y }));
    setEditing({ ...editing, formation, starting_xi: newXI });
  };

  const assignSlot = (idx, playerId) =>
    setEditing((e) => ({ ...e, starting_xi: e.starting_xi.map((s, i) => i === idx ? { ...s, player_id: playerId } : s) }));

  const moveToken = (id, x, y) =>
    setEditing((e) => ({ ...e, starting_xi: e.starting_xi.map((s) => s.player_id === id ? { ...s, x, y } : s) }));

  const upsertEval = (item) =>
    setEditing((e) => {
      const exists = e.evaluations.find((x) => x.player_id === item.player_id);
      const evals = exists ? e.evaluations.map((x) => x.player_id === item.player_id ? { ...x, ...item } : x) : [...e.evaluations, item];
      return { ...e, evaluations: evals };
    });
  const removeEval = (pid) => setEditing((e) => ({ ...e, evaluations: e.evaluations.filter((x) => x.player_id !== pid) }));

  const addEvent = (key, item) => setEditing((e) => ({ ...e, [key]: [...e[key], item] }));
  const removeEvent = (key, idx) => setEditing((e) => ({ ...e, [key]: e[key].filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...editing,
      our_score: editing.our_score === "" ? null : parseInt(editing.our_score),
      opp_score: editing.opp_score === "" ? null : parseInt(editing.opp_score),
      squad: editing.starting_xi.filter((s) => s.player_id).map((s) => s.player_id).concat(editing.substitutes),
      evaluations: editing.evaluations
        .filter((e) => e.rating !== "" || e.note)
        .map((e) => ({ ...e, rating: e.rating === "" ? null : parseInt(e.rating) })),
    };
    try {
      await api.put(`/matches/${editing.id}`, payload);
      toast.success("تم حفظ المباراة");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف المباراة؟")) return;
    await api.delete(`/matches/${id}`);
    load();
  };

  const nameOf = (id) => players.find((p) => p.id === id)?.name || "—";
  const inLineup = editing?.starting_xi?.map((s) => s.player_id).filter(Boolean) || [];
  const benchPool = players.filter((p) => !inLineup.includes(p.id));

  const tokens = (editing?.starting_xi || [])
    .filter((s) => s.player_id)
    .map((s) => {
      const pl = players.find((p) => p.id === s.player_id);
      return { id: s.player_id, number: pl?.jersey_number, name: pl?.name, status: pl?.status, x: s.x, y: s.y };
    });

  const TABS = [
    { k: "info", label: "المعلومات" },
    { k: "lineup", label: "التشكيلة" },
    { k: "events", label: "الأحداث" },
    { k: "ratings", label: "الملاحظات والتقييم" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-medium text-[#2a3b19] uppercase tracking-wider">المنافسات</p>
          <h1 className="text-3xl font-black">المباريات</h1>
        </div>
        <button data-testid="add-match-btn" onClick={startCreate} className="px-5 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center gap-2">
          <CirclePlus size={18} /> مباراة جديدة
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {matches.map((m) => (
          <div key={m.id} data-testid={`match-card-${m.id}`} className="glass border border-[#d1fae5] rounded-2xl p-5 hover:border-[#2a3b19]/40 transition-colors cursor-pointer" onClick={() => openDetails(m)}>
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl bg-[#2a3b19]/10 flex items-center justify-center"><Medal className="text-[#2a3b19]" size={22} /></div>
              <button onClick={(e) => { e.stopPropagation(); remove(m.id); }} className="text-red-500 hover:text-red-400" data-testid={`delete-match-${m.id}`}><Trash size={16} /></button>
            </div>
            <h3 className="font-bold mt-3">ضد {m.opponent || "—"}</h3>
            {(m.our_score != null || m.opp_score != null) && (
              <div className="text-2xl font-black text-[#2a3b19] my-1">{m.our_score ?? 0} - {m.opp_score ?? 0}</div>
            )}
            <div className="text-xs text-slate-600 mt-1 space-y-1">
              <div>{m.tournament}</div>
              <div>{m.date} {m.time && `· ${m.time}`}</div>
              {m.stadium && <div className="flex items-center gap-1"><MapPinned size={12} /> {m.stadium}</div>}
              <div className="text-[#2a3b19]">{m.formation}</div>
            </div>
          </div>
        ))}
        {matches.length === 0 && <p className="text-slate-500 col-span-full text-center py-12">لا توجد مباريات بعد.</p>}
      </div>

      {/* STEP 1: minimal create modal */}
      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setCreating(false)}>
          <div onClick={(e) => e.stopPropagation()} className="glass border border-[#d1fae5] rounded-2xl p-6 w-full max-w-md" data-testid="match-create-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">إنشاء مباراة</h2>
              <button onClick={() => setCreating(false)}><X size={22} className="text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="المنافس" data-testid="mt-opponent" value={createForm.opponent} onChange={(e) => setCreateForm({ ...createForm, opponent: e.target.value })} className={inputCls} />
              <input placeholder="البطولة" value={createForm.tournament} onChange={(e) => setCreateForm({ ...createForm, tournament: e.target.value })} className={inputCls} />
              <input placeholder="الملعب" value={createForm.stadium} onChange={(e) => setCreateForm({ ...createForm, stadium: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={createForm.date} onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })} className={inputCls} />
                <input type="time" value={createForm.time} onChange={(e) => setCreateForm({ ...createForm, time: e.target.value })} className={inputCls} />
              </div>
              <select value={createForm.formation} onChange={(e) => setCreateForm({ ...createForm, formation: e.target.value })} className={inputCls} data-testid="mt-formation">
                {Object.keys(FORMATIONS).map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <button onClick={createMatch} disabled={saving} data-testid="create-match-btn" className="w-full mt-5 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center justify-center gap-2">
              {saving && <LoaderCircle className="animate-spin" size={18} />} إنشاء ومتابعة التفاصيل
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: details editor */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="glass border border-[#d1fae5] rounded-2xl p-6 w-full max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="match-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">ضد {editing.opponent || "—"}</h2>
              <button onClick={() => setEditing(null)}><X size={22} className="text-slate-500" /></button>
            </div>

            <div className="flex gap-2 mb-5 border-b border-[#d1fae5] overflow-x-auto">
              {TABS.map((t) => (
                <button key={t.k} onClick={() => setTab(t.k)} data-testid={`match-tab-${t.k}`} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === t.k ? "border-[#2a3b19] text-[#2a3b19]" : "border-transparent text-slate-600"}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "info" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <input placeholder="المنافس" value={editing.opponent} onChange={(e) => setEditing({ ...editing, opponent: e.target.value })} className={inputCls} />
                <input placeholder="البطولة" value={editing.tournament} onChange={(e) => setEditing({ ...editing, tournament: e.target.value })} className={inputCls} />
                <input placeholder="الملعب" value={editing.stadium} onChange={(e) => setEditing({ ...editing, stadium: e.target.value })} className={inputCls} />
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className={inputCls} />
                  <input type="time" value={editing.time} onChange={(e) => setEditing({ ...editing, time: e.target.value })} className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" placeholder="أهدافنا" data-testid="mt-ourscore" value={editing.our_score} onChange={(e) => setEditing({ ...editing, our_score: e.target.value })} className={inputCls} />
                  <input type="number" placeholder="أهداف المنافس" value={editing.opp_score} onChange={(e) => setEditing({ ...editing, opp_score: e.target.value })} className={inputCls} />
                </div>
                <textarea placeholder="ملاحظات عامة على المباراة" value={editing.general_notes} onChange={(e) => setEditing({ ...editing, general_notes: e.target.value })} className={inputCls + " sm:col-span-2"} rows={2} data-testid="mt-general-notes" />
              </div>
            )}

            {tab === "lineup" && (
              <div className="grid lg:grid-cols-2 gap-5">
                <div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {Object.keys(FORMATIONS).map((f) => (
                      <button key={f} onClick={() => applyFormation(f)} data-testid={`formation-${f}`} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${editing.formation === f && editing.starting_xi.length ? "bg-[#2a3b19] text-white" : "bg-[#ecfdf5] text-slate-700"}`}>{f}</button>
                    ))}
                  </div>
                  {editing.starting_xi.length ? (
                    <PitchBoard players={tokens} onMove={moveToken} draggable />
                  ) : (
                    <div className="text-center text-slate-500 text-sm py-16 border border-dashed border-[#d1fae5] rounded-2xl">اختر خطة لإظهار الملعب</div>
                  )}
                  <p className="text-xs text-slate-500 mt-2 text-center">اسحب اللاعبين لتعديل مراكزهم</p>
                </div>
                <div>
                  <h3 className="font-bold mb-2 text-sm">تعيين اللاعبين للمراكز ({editing.starting_xi.length})</h3>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto pl-1">
                    {editing.starting_xi.map((s, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs w-6 text-slate-500">{i === 0 ? "حارس" : i}</span>
                        <select value={s.player_id || ""} onChange={(e) => assignSlot(i, e.target.value || null)} className={inputCls} data-testid={`slot-${i}`}>
                          <option value="">— فارغ —</option>
                          {players.map((p) => (
                            <option key={p.id} value={p.id} disabled={inLineup.includes(p.id) && s.player_id !== p.id}>
                              {p.jersey_number ? `${p.jersey_number} - ` : ""}{p.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                  <h3 className="font-bold mt-4 mb-2 text-sm">لاعبو الاحتياط</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {benchPool.map((p) => {
                      const on = editing.substitutes.includes(p.id);
                      return (
                        <button key={p.id} data-testid={`sub-${p.id}`} onClick={() => setEditing({ ...editing, substitutes: on ? editing.substitutes.filter((x) => x !== p.id) : [...editing.substitutes, p.id] })} className={`text-xs px-2.5 py-1.5 rounded-lg ${on ? "bg-[#2a3b19] text-white" : "bg-[#ecfdf5] text-slate-600"}`}>
                          {p.name}
                        </button>
                      );
                    })}
                  </div>
                  <textarea placeholder="الخطة والتكتيك المستخدم" value={editing.tactic_notes} onChange={(e) => setEditing({ ...editing, tactic_notes: e.target.value })} className={inputCls + " mt-3"} rows={2} />
                </div>
              </div>
            )}

            {tab === "events" && (
              <div className="space-y-5">
                <EventSection title="الأهداف" icon={Goal} color="text-[#2a3b19]"
                  items={editing.goals} render={(g) => `${nameOf(g.player_id)} ${g.minute ? `- د${g.minute}` : ""}`}
                  onRemove={(i) => removeEvent("goals", i)}
                  form={<EventAdder players={players} withMinute onAdd={(pid, minute) => addEvent("goals", { player_id: pid, minute })} testid="goal" />} />
                <EventSection title="البطاقات" icon={RectangleVertical} color="text-[#2a3b19]"
                  items={editing.cards} render={(c) => `${nameOf(c.player_id)} - ${c.type === "red" ? "🟥 حمراء" : "🟨 صفراء"} ${c.minute ? `د${c.minute}` : ""}`}
                  onRemove={(i) => removeEvent("cards", i)}
                  form={<CardAdder players={players} onAdd={(pid, type, minute) => addEvent("cards", { player_id: pid, type, minute })} />} />
                <EventSection title="التبديلات" icon={Replace} color="text-[#2a3b19]"
                  items={editing.substitutions} render={(s) => `خروج ${nameOf(s.out)} ← دخول ${nameOf(s.in)} ${s.minute ? `د${s.minute}` : ""}`}
                  onRemove={(i) => removeEvent("substitutions", i)}
                  form={<SubAdder players={players} onAdd={(out, inn, minute) => addEvent("substitutions", { out, in: inn, minute })} />} />
              </div>
            )}

            {tab === "ratings" && (
              <div>
                <p className="text-sm text-slate-600 mb-3">اختر لاعباً محدداً لإضافة ملاحظة وتقييم له في هذه المباراة.</p>
                <PlayerNoteAdder players={players} evaluations={editing.evaluations} onSave={upsertEval} onRemove={removeEval} testidPrefix="mt-eval" />
                {players.length === 0 && <p className="text-slate-500 text-sm mt-2">أضف لاعبين أولاً.</p>}
              </div>
            )}

            <button onClick={save} disabled={saving} data-testid="save-match-btn" className="w-full mt-6 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center justify-center gap-2">
              {saving && <LoaderCircle className="animate-spin" size={18} />} حفظ التفاصيل
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EventSection({ title, icon: Icon, color, items, render, onRemove, form }) {
  return (
    <div>
      <h3 className={`font-bold mb-2 flex items-center gap-2 ${color}`}><Icon size={16} /> {title}</h3>
      {form}
      <div className="space-y-1.5 mt-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between bg-white border border-[#d1fae5] rounded-lg px-3 py-2 text-sm">
            <span>{render(it)}</span>
            <button onClick={() => onRemove(i)} className="text-red-500"><X size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const smallInput = "px-3 py-2 rounded-lg bg-white border border-[#d1fae5] focus:border-[#2a3b19] outline-none text-sm";

function EventAdder({ players, onAdd, withMinute, testid }) {
  const [pid, setPid] = useState("");
  const [minute, setMinute] = useState("");
  return (
    <div className="flex gap-2">
      <select value={pid} onChange={(e) => setPid(e.target.value)} className={smallInput + " flex-1"} data-testid={`${testid}-player`}>
        <option value="">اللاعب</option>
        {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {withMinute && <input type="number" placeholder="د" value={minute} onChange={(e) => setMinute(e.target.value)} className={smallInput + " w-16"} />}
      <button onClick={() => { if (pid) { onAdd(pid, minute); setPid(""); setMinute(""); } }} className="px-4 rounded-lg bg-[#ecfdf5] hover:bg-[#d1fae5]" data-testid={`${testid}-add`}><CirclePlus size={16} /></button>
    </div>
  );
}

function CardAdder({ players, onAdd }) {
  const [pid, setPid] = useState("");
  const [type, setType] = useState("yellow");
  const [minute, setMinute] = useState("");
  return (
    <div className="flex gap-2">
      <select value={pid} onChange={(e) => setPid(e.target.value)} className={smallInput + " flex-1"} data-testid="card-player">
        <option value="">اللاعب</option>
        {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select value={type} onChange={(e) => setType(e.target.value)} className={smallInput}>
        <option value="yellow">صفراء</option>
        <option value="red">حمراء</option>
      </select>
      <input type="number" placeholder="د" value={minute} onChange={(e) => setMinute(e.target.value)} className={smallInput + " w-16"} />
      <button onClick={() => { if (pid) { onAdd(pid, type, minute); setPid(""); setMinute(""); } }} className="px-4 rounded-lg bg-[#ecfdf5] hover:bg-[#d1fae5]" data-testid="card-add"><CirclePlus size={16} /></button>
    </div>
  );
}

function SubAdder({ players, onAdd }) {
  const [out, setOut] = useState("");
  const [inn, setInn] = useState("");
  const [minute, setMinute] = useState("");
  return (
    <div className="flex gap-2 flex-wrap">
      <select value={out} onChange={(e) => setOut(e.target.value)} className={smallInput + " flex-1"} data-testid="sub-out">
        <option value="">خروج</option>
        {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select value={inn} onChange={(e) => setInn(e.target.value)} className={smallInput + " flex-1"} data-testid="sub-in">
        <option value="">دخول</option>
        {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <input type="number" placeholder="د" value={minute} onChange={(e) => setMinute(e.target.value)} className={smallInput + " w-16"} />
      <button onClick={() => { if (out && inn) { onAdd(out, inn, minute); setOut(""); setInn(""); setMinute(""); } }} className="px-4 rounded-lg bg-[#ecfdf5] hover:bg-[#d1fae5]" data-testid="sub-add"><CirclePlus size={16} /></button>
    </div>
  );
}
